/**
 * Stats View — full statistics dashboard with rates, charts, totals, and zone breakdown
 */

import { useState } from 'react';
import { useStats } from '@/state';
import { useStatRates } from '@/hooks/useStatRates';
import { ZONE_DEFINITIONS } from '@/data';
import { Quality, QualityColors, QualityNames } from '@/types';
import { formatCurrency, formatRate } from '@/utils';
import { ScrollableView } from '@/components/layout/ScrollableView';
import { LineChart, DataPoint } from '@/components/charts/LineChart';
import { HistoryPoint } from '@/systems/stats/StatTracker';

const DISPLAY_QUALITIES = [
  Quality.COMMON,
  Quality.UNCOMMON,
  Quality.RARE,
  Quality.EPIC,
  Quality.LEGENDARY,
  Quality.UNIQUE,
] as const;

const TIME_RANGES = [
  { label: '1m', seconds: 60 },
  { label: '10m', seconds: 600 },
  { label: '30m', seconds: 1800 },
  { label: '1h', seconds: 3600 },
] as const;

function historyToDataPoints(history: HistoryPoint[], field: 'fishPerHour' | 'currencyPerHour'): DataPoint[] {
  return history.map((p) => ({ t: p.timestamp, v: p[field] }));
}

export function StatsView() {
  const stats = useStats();
  const [rangeIndex, setRangeIndex] = useState(0);
  const selectedRange = TIME_RANGES[rangeIndex];
  const { global, byZone, history, historyByZone } = useStatRates(1000, selectedRange.seconds);

  const fishChartData = historyToDataPoints(history, 'fishPerHour');
  const goldChartData = historyToDataPoints(history, 'currencyPerHour');
  const timeRangeMs = selectedRange.seconds * 1000;

  return (
    <ScrollableView gap={8}>
      {/* Production Rates */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Production Rates</h2>
          <div className="flex gap-1" data-testid="time-range-toggle">
            {TIME_RANGES.map((range, i) => (
              <button
                key={range.label}
                onClick={() => setRangeIndex(i)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  i === rangeIndex
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                }`}
                data-testid={`range-${range.label}`}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <RateCard
            label="Fish / Hour"
            value={formatRate(global.fishPerHour)}
            icon="🐟"
            color="text-blue-400"
          />
          <RateCard
            label="Gold / Hour"
            value={formatRate(global.currencyPerHour)}
            icon="💰"
            color="text-yellow-400"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-800 rounded-lg p-2">
            <LineChart
              data={fishChartData}
              height={70}
              color="#60a5fa"
              label="Fish/hr"
              timeRangeMs={timeRangeMs}
            />
          </div>
          <div className="bg-gray-800 rounded-lg p-2">
            <LineChart
              data={goldChartData}
              height={70}
              color="#facc15"
              label="Gold/hr"
              timeRangeMs={timeRangeMs}
            />
          </div>
        </div>
      </section>

      {/* All-Time Totals */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-3">All-Time Totals</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Fish Caught" value={stats.totalFishCaught.toLocaleString()} />
          <StatCard label="Fish Sold" value={stats.totalFishSold.toLocaleString()} />
          <StatCard label="Gold Earned" value={formatCurrency(stats.totalCurrencyEarned)} />
          <StatCard label="Clicks" value={stats.totalClicks.toLocaleString()} />
        </div>
      </section>

      {/* Zone Breakdown */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-3">Zone Breakdown</h2>
        <div className="space-y-3">
          {ZONE_DEFINITIONS.map((zone) => {
            const zoneStats = stats.statsByZone[zone.id];
            const zoneRates = byZone[zone.id];
            const zoneHistory = historyByZone[zone.id] ?? [];
            return (
              <ZoneStatsCard
                key={zone.id}
                zoneId={zone.id}
                zoneName={zone.name}
                totalCaught={zoneStats?.totalFishCaught ?? 0}
                totalSold={zoneStats?.totalFishSold ?? 0}
                totalEarned={zoneStats?.totalCurrencyEarned ?? 0}
                fishPerHour={zoneRates?.fishPerHour ?? 0}
                goldPerHour={zoneRates?.currencyPerHour ?? 0}
                fishHistory={historyToDataPoints(zoneHistory, 'fishPerHour')}
                goldHistory={historyToDataPoints(zoneHistory, 'currencyPerHour')}
                timeRangeMs={timeRangeMs}
              />
            );
          })}
        </div>
      </section>

      {/* Quality Breakdown */}
      <section>
        <h2 className="text-lg font-semibold text-white mb-3">Catches by Quality</h2>
        <div className="bg-gray-800 rounded-lg p-4 space-y-2">
          {DISPLAY_QUALITIES.map((quality) => (
            <div key={quality} className="flex justify-between items-center">
              <span className="font-medium" style={{ color: QualityColors[quality] }}>
                {QualityNames[quality]}
              </span>
              <span className="text-gray-300 tabular-nums">
                {(stats.fishCaughtByQuality[quality] ?? 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </section>
    </ScrollableView>
  );
}

function RateCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: string;
  color: string;
}) {
  return (
    <div className="bg-gray-800 rounded-lg p-4 text-center">
      <div className="text-2xl mb-1">{icon}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-sm text-gray-400 mt-1">{label}</div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-800 rounded-lg p-3 text-center">
      <div className="text-lg font-semibold text-white">{value}</div>
      <div className="text-xs text-gray-400 mt-1">{label}</div>
    </div>
  );
}

function ZoneStatsCard({
  zoneId,
  zoneName,
  totalCaught,
  totalSold,
  totalEarned,
  fishPerHour,
  goldPerHour,
  fishHistory,
  goldHistory,
  timeRangeMs,
}: {
  zoneId: string;
  zoneName: string;
  totalCaught: number;
  totalSold: number;
  totalEarned: number;
  fishPerHour: number;
  goldPerHour: number;
  fishHistory: DataPoint[];
  goldHistory: DataPoint[];
  timeRangeMs: number;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-gray-800 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full text-left p-4 hover:bg-gray-750 transition-colors"
        data-testid={`zone-toggle-${zoneId}`}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-white">{zoneName}</h3>
          <span
            className={`text-gray-400 text-xs transition-transform ${expanded ? 'rotate-180' : ''}`}
            data-testid={`zone-chevron-${zoneId}`}
          >
            ▼
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm">
          <div>
            <span className="text-gray-400">Caught: </span>
            <span className="text-white">{totalCaught.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-400">Sold: </span>
            <span className="text-white">{totalSold.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-400">Earned: </span>
            <span className="text-yellow-400">{formatCurrency(totalEarned)}</span>
          </div>
          <div>
            <span className="text-gray-400">Fish/hr: </span>
            <span className="text-blue-400">{formatRate(fishPerHour)}</span>
          </div>
          <div>
            <span className="text-gray-400">Gold/hr: </span>
            <span className="text-yellow-400">{formatRate(goldPerHour)}</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div
          className="px-4 pb-4 grid grid-cols-2 gap-2 border-t border-gray-700 pt-3"
          data-testid={`zone-charts-${zoneId}`}
        >
          <LineChart
            data={fishHistory}
            height={70}
            color="#60a5fa"
            label="Fish/hr"
            timeRangeMs={timeRangeMs}
          />
          <LineChart
            data={goldHistory}
            height={70}
            color="#facc15"
            label="Gold/hr"
            timeRangeMs={timeRangeMs}
          />
        </div>
      )}
    </div>
  );
}
